/**
 * @description - The utility is used to get the font size/value, in order to be responsible for the layout size.
 */

// ================================================================================================================== //
// ===================================================== MODULES ==================================================== //
// ================================================================================================================== //
// Material UI
import { useTheme } from '@mui/material/styles';
import { Variant } from '@mui/material/styles/createTypography';
// Hooks
import { useResponsive } from 'src/hooks';

// ================================================================================================================== //
// ====================================================== LOGIC ===================================================== //
// ================================================================================================================== //

/**
 * @description - The method is returning the font's values (sizes), based on the provided layout.
 * @param variant
 */
export default function getFontValue(variant: Variant) {
  const theme = useTheme();
  const breakpoints = useWidth();

  const key = theme.breakpoints.up(breakpoints === 'xl' ? 'lg' : breakpoints);

  const hasResponsive =
    variant === 'h1' ||
    variant === 'h2' ||
    variant === 'h3' ||
    variant === 'h4' ||
    variant === 'h5' ||
    variant === 'h6';

  const getFont: any =
    hasResponsive && theme.typography[variant][key]
      ? theme.typography[variant][key]
      : theme.typography[variant];

  const fontSize = remToPx(getFont.fontSize);
  const lineHeight = Number(theme.typography[variant].lineHeight) * fontSize;
  const { fontWeight, letterSpacing } = theme.typography[variant];

  return { fontSize, lineHeight, fontWeight, letterSpacing };
}

/**
 * @description - The converter method to transform the rem to px
 * @param value
 */
export function remToPx(value: string) {
  return Math.round(parseFloat(value) * 16);
}

/**
 * @description - The converter method to transform the px to rem
 * @param value
 */
export function pxToRem(value: number) {
  return `${value / 16}rem`;
}

/**
 * @description - The method is parsing the layout size in order to return the responsible font size
 * @param sm
 * @param md
 * @param lg
 */
export function responsiveFontSizes({ sm, md, lg }: { sm: number; md: number; lg: number }) {
  return {
    '@media (min-width:600px)': {
      fontSize: pxToRem(sm),
    },
    '@media (min-width:900px)': {
      fontSize: pxToRem(md),
    },
    '@media (min-width:1200px)': {
      fontSize: pxToRem(lg),
    },
  };
}

/**
 * @description - The method is checking the breakpoint as the fixed with in order to get the responsible font size.
 */
function useWidth() {
  const theme = useTheme();
  const keys = [...theme.breakpoints.keys].reverse();
  return (
    // @ts-ignore not sure what is this
    keys.reduce((output, key) => {
      // eslint-disable-next-line react-hooks/rules-of-hooks
      const matches = useResponsive('up', key);
      return !output && matches ? key : output;
    }, null) || 'xs'
  );
}
